import React, { useState, useEffect, useRef } from 'react';
import {
    X,
    Mail,
    Edit3,
    Send,
    Clock,
    ArrowLeft,
    RotateCcw,
    Eye,
    Calendar,
    ChevronDown,
    FileText,
    Type,
    Save
} from 'lucide-react';
import RichTextEditor from './RichTextEditor';
import api from '../utils/api';

const EmailPreviewModal = ({
    isOpen,
    onClose,
    onSend,
    employeeData,
    companyData,
    isPreboarding,
    isUserInvitation = false
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [showScheduleOptions, setShowScheduleOptions] = useState(false);
    const [loading, setLoading] = useState(false);

    const [emailContent, setEmailContent] = useState({
        subject: '',
        greeting: '',
        body: '',
        closing: '',
        footer: '',
    });

    const [originalContent, setOriginalContent] = useState(null);
    const [scheduleDate, setScheduleDate] = useState('');
    const [scheduleTime, setScheduleTime] = useState('09:00');

    // Template selection state
    const [templates, setTemplates] = useState([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState(null);
    const [templatesLoading, setTemplatesLoading] = useState(false);

    // Placeholder dropdown
    const [showPlaceholders, setShowPlaceholders] = useState(false);
    const [activeField, setActiveField] = useState('body');
    const fieldRefs = {
        subject: useRef(null),
        greeting: useRef(null),
        body: useRef(null),
        closing: useRef(null),
        footer: useRef(null),
    };

    const PLACEHOLDERS = [
        { label: 'Employee First Name', value: '{employee.first_name}' },
        { label: 'Employee Full Name', value: '{employee.full_name}' },
        { label: 'Employee Email', value: '{employee.email}' },
        { label: 'Start Date', value: '{employee.start_date}' },
        { label: 'Team', value: '{employee.team}' },
        { label: 'Manager Name', value: '{employee.manager.name}' },
        { label: 'Company Name', value: '{company.name}' },
        { label: 'Company Website', value: '{company.website}' },
        { label: 'Sender Name', value: '{sender.name}' },
        { label: 'Sender Title', value: '{sender.title}' },
        { label: 'Sender Email', value: '{sender.email}' },
    ];

    // Determine template type from props
    const getTemplateType = () => {
        if (isUserInvitation) return 'active';
        if (isPreboarding) return 'preboarding';
        return 'onboarding';
    };

    // Substitute placeholders in template text
    const substitutePlaceholders = (text) => {
        if (!text) return text;
        const companyName = companyData?.company_name || 'Our Company';
        const startDate = isPreboarding
            ? employeeData?.employee_actual_start_date || employeeData?.start_date
            : employeeData?.start_date;
        return text
            .replace(/\{employee\.first_name\}/g, employeeData?.first_name || '')
            .replace(/\{employee\.full_name\}/g, `${employeeData?.first_name || ''} ${employeeData?.last_name || ''}`)
            .replace(/\{employee\.email\}/g, employeeData?.email || '')
            .replace(/\{employee\.start_date\}/g, formatDate(startDate))
            .replace(/\{employee\.team\}/g, employeeData?.team_name || '')
            .replace(/\{employee\.manager\.name\}/g, employeeData?.manager_name || '')
            .replace(/\{company\.name\}/g, companyName)
            .replace(/\{company\.website\}/g, companyData?.company_website || '')
            .replace(/\{sender\.name\}/g, '')
            .replace(/\{sender\.title\}/g, '')
            .replace(/\{sender\.email\}/g, '');
    };

    // Convert a saved template to emailContent format with placeholders
    const templateToEmailContent = (tpl) => {
        return {
            subject: tpl.subject || '',
            greeting: tpl.greeting || '',
            body: tpl.body || '',
            closing: tpl.closing || '',
            footer: tpl.signature
                ? tpl.signature
                : `<div style="margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; color: #6b7280; font-size: 12px;"><p>{company.name}<br/>{company.website}</p></div>`,
        };
    };

    // Fetch templates matching the lifecycle type
    useEffect(() => {
        if (isOpen && employeeData) {
            const type = getTemplateType();
            setTemplatesLoading(true);
            api.get(`/email-templates?type=${type}`)
                .then(res => {
                    const tpls = res.data.templates || [];
                    setTemplates(tpls);
                    // Auto-select default template
                    const defaultTpl = tpls.find(t => t.is_default);
                    if (defaultTpl) {
                        setSelectedTemplateId(defaultTpl.id);
                        const content = templateToEmailContent(defaultTpl);
                        setEmailContent(content);
                        setOriginalContent(content);
                    } else {
                        // No custom templates — use hardcoded
                        setSelectedTemplateId(null);
                        const template = generateEmailTemplate();
                        setEmailContent(template);
                        setOriginalContent(template);
                    }
                })
                .catch(() => {
                    // Fetch failed — use hardcoded templates
                    setSelectedTemplateId(null);
                    const template = generateEmailTemplate();
                    setEmailContent(template);
                    setOriginalContent(template);
                })
                .finally(() => setTemplatesLoading(false));

            // Set default schedule date
            if (!isUserInvitation) {
                if (isPreboarding && employeeData.preboarding_start_date) {
                    setScheduleDate(employeeData.preboarding_start_date);
                } else if (employeeData.employee_actual_start_date) {
                    setScheduleDate(employeeData.employee_actual_start_date);
                }
            }
        }
    }, [isOpen, employeeData, isPreboarding, isUserInvitation]);

    // Handle template selection change
    const handleTemplateChange = (templateId) => {
        if (templateId === 'default') {
            setSelectedTemplateId(null);
            const template = generateEmailTemplate();
            setEmailContent(template);
            setOriginalContent(template);
        } else {
            const tpl = templates.find(t => t.id === parseInt(templateId));
            if (tpl) {
                setSelectedTemplateId(tpl.id);
                const content = templateToEmailContent(tpl);
                setEmailContent(content);
                setOriginalContent(content);
            }
        }
        setIsEditing(false);
    };

    const generateEmailTemplate = () => {
        // User invitation template (non-onboarding users)
        if (isUserInvitation) {
            return {
                subject: `Welcome to {company.name} - Set Up Your Account`,
                greeting: `Dear {employee.first_name},`,
                body: `<p>Your account has been created at <strong>{company.name}</strong>.</p>

<p>You have been added as a <strong>{employee.team}</strong>. Please set up your password to access the system.</p>

<h3>Getting Started:</h3>
<ul>
  <li><strong>Set Your Password:</strong> Click the button below to create your password</li>
  <li><strong>Access Your Portal:</strong> Login and explore your dashboard</li>
  <li><strong>Review Resources:</strong> Check out available documents and tools</li>
</ul>

<p>Click the button below to set up your account:</p>

<div style="text-align: center; margin: 30px 0;">
  <a href="[SETUP_LINK]" style="background-color: #f97316; color: white; padding: 12px 30px; text-decoration: none; border-radius: 8px; display: inline-block; font-weight: 600;">
    Set Up Password
  </a>
</div>

<p>If you have any questions, feel free to reach out!</p>`,
                closing: `<p>Welcome aboard!</p>
<p>Best regards,<br/>
<strong>The {company.name} Team</strong></p>`,
                footer: `<div style="margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; color: #6b7280; font-size: 12px;">
  <p>{company.name}<br/>
  {company.website}</p>
</div>`,
            };
        }

        if (isPreboarding) {
            return {
                subject: `Welcome to {company.name} - Your Preboarding Journey Begins! 🎉`,
                greeting: `Dear {employee.first_name},`,
                body: `<p>We are thrilled to welcome you to the <strong>{company.name}</strong> family!</p>

<p>Your official start date is <strong>{employee.start_date}</strong>, but we wanted to get you started early with our preboarding program. This will help you hit the ground running when you join us!</p>

<h3>What's Next?</h3>
<ul>
  <li><strong>Complete Your Profile:</strong> Set up your account and add your details</li>
  <li><strong>Explore Resources:</strong> Get familiar with our company culture and policies</li>
  <li><strong>Meet the Team:</strong> Learn about your colleagues and department</li>
  <li><strong>Complete Initial Tasks:</strong> Finish any preboarding assignments</li>
</ul>

<p>To get started, please click the button below to access your preboarding portal:</p>

<div style="text-align: center; margin: 30px 0;">
  <a href="[PREBOARDING_LINK]" style="background-color: #3b82f6; color: white; padding: 12px 30px; text-decoration: none; border-radius: 8px; display: inline-block; font-weight: 600;">
    Start Preboarding
  </a>
</div>

<p>If you have any questions before your start date, please don't hesitate to reach out!</p>`,
                closing: `<p>We can't wait to have you on board!</p>
<p>Best regards,<br/>
<strong>The {company.name} Team</strong></p>`,
                footer: `<div style="margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; color: #6b7280; font-size: 12px;">
  <p>{company.name}<br/>
  {company.website}</p>
</div>`,
            };
        } else {
            return {
                subject: `Welcome to {company.name} - Let's Get You Started! 🚀`,
                greeting: `Dear {employee.first_name},`,
                body: `<p>Welcome to <strong>{company.name}</strong>! We're excited to have you joining us.</p>

<p>Your start date is <strong>{employee.start_date}</strong>, and we want to make sure you have everything you need for a smooth first day.</p>

<h3>Before You Start:</h3>
<ul>
  <li><strong>Complete Your Onboarding:</strong> Set up your account and complete required paperwork</li>
  <li><strong>Review Important Documents:</strong> Employee handbook, policies, and procedures</li>
  <li><strong>Set Up Your Profile:</strong> Add your information and preferences</li>
</ul>

<p>To begin your onboarding process, click the button below:</p>

<div style="text-align: center; margin: 30px 0;">
  <a href="[ONBOARDING_LINK]" style="background-color: #3b82f6; color: white; padding: 12px 30px; text-decoration: none; border-radius: 8px; display: inline-block; font-weight: 600;">
    Start Onboarding
  </a>
</div>

<p>We're here to support you every step of the way!</p>`,
                closing: `<p>Looking forward to working with you!</p>
<p>Best regards,<br/>
<strong>The {company.name} Team</strong></p>`,
                footer: `<div style="margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; color: #6b7280; font-size: 12px;">
  <p>{company.name}<br/>
  {company.website}</p>
</div>`,
            };
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'TBD';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const handleResetToDefault = () => {
        if (window.confirm('Are you sure you want to reset the email to the default template? All changes will be lost.')) {
            const template = generateEmailTemplate();
            setEmailContent(template);
            setIsEditing(false);
        }
    };

    const insertPlaceholder = (placeholder) => {
        const ref = fieldRefs[activeField]?.current;

        if (ref && typeof ref.insertText === 'function') {
            // It's a RichTextEditor component
            ref.insertText(placeholder);
        } else if (ref && (ref instanceof HTMLInputElement || ref instanceof HTMLTextAreaElement)) {
            // It's a normal text input/textarea
            const start = ref.selectionStart || 0;
            const end = ref.selectionEnd || 0;
            const current = emailContent[activeField] || '';
            const newValue = current.substring(0, start) + placeholder + current.substring(end);

            setEmailContent(prev => ({
                ...prev,
                [activeField]: newValue
            }));

            // Restore cursor
            setTimeout(() => {
                ref.focus();
                const newPos = start + placeholder.length;
                ref.setSelectionRange(newPos, newPos);
            }, 0);
        } else {
            // Fallback
            setEmailContent(prev => ({
                ...prev,
                [activeField]: prev[activeField] + placeholder
            }));
        }
        setShowPlaceholders(false);
    };

    const PlaceholderDropdown = ({ field }) => (
        <div className="relative inline-block ml-3 font-normal">
            <button
                type="button"
                onClick={(e) => {
                    e.preventDefault();
                    setActiveField(field);
                    setShowPlaceholders(showPlaceholders === field ? false : field);
                }}
                className="flex items-center gap-1.5 px-2 py-1 bg-info/10 text-info rounded-md text-xs font-medium hover:bg-info/20 transition-colors"
                title={`Insert placeholder into ${field}`}
            >
                <Type size={12} />
                Insert Placeholder
                <ChevronDown size={12} className={`transition-transform ${showPlaceholders === field ? 'rotate-180' : ''}`} />
            </button>

            {showPlaceholders === field && (
                <div className="absolute z-20 mt-1 left-0 w-64 bg-card border border-border rounded-xl shadow-xl py-2 max-h-64 overflow-y-auto">
                    <div className="px-3 py-1.5 text-[10px] text-muted-foreground font-medium uppercase tracking-wider">
                        Insert into: {field}
                    </div>
                    {PLACEHOLDERS.map(p => (
                        <button
                            key={p.value}
                            type="button"
                            onClick={(e) => {
                                e.preventDefault();
                                setActiveField(field);
                                insertPlaceholder(p.value);
                            }}
                            className="w-full text-left px-3 py-1.5 hover:bg-secondary transition-colors flex items-center justify-between"
                        >
                            <span className="text-xs text-foreground">{p.label}</span>
                            <code className="text-[10px] text-muted-foreground bg-secondary rounded px-1 py-0.5">{p.value}</code>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );

    const getSubstitutedContent = () => {
        return {
            subject: substitutePlaceholders(emailContent.subject),
            greeting: substitutePlaceholders(emailContent.greeting),
            body: substitutePlaceholders(emailContent.body),
            closing: substitutePlaceholders(emailContent.closing),
            footer: substitutePlaceholders(emailContent.footer),
        };
    };

    const handleSendNow = async () => {
        setLoading(true);
        try {
            await onSend({
                emailContent: getSubstitutedContent(),
                sendType: 'immediate',
            });
        } catch (error) {
            console.error('Error sending email:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleScheduleSend = async () => {
        if (!scheduleDate || !scheduleTime) {
            alert('Please select a date and time for scheduling');
            return;
        }

        setLoading(true);
        try {
            const scheduledDateTime = `${scheduleDate}T${scheduleTime}:00`;
            await onSend({
                emailContent: getSubstitutedContent(),
                sendType: 'scheduled',
                scheduledDateTime,
            });
        } catch (error) {
            console.error('Error scheduling email:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSaveAsTemplate = async () => {
        const name = window.prompt("Enter a name for this template:");
        if (!name) return;

        try {
            const res = await api.post('/email-templates', {
                name,
                template_type: getTemplateType(),
                subject: emailContent.subject,
                greeting: emailContent.greeting,
                body: emailContent.body,
                closing: emailContent.closing,
                signature: emailContent.footer,
                is_default: false
            });
            alert('Template saved successfully!');

            // Add new template to list and select it
            const newTpl = res.data.template;
            setTemplates(prev => [newTpl, ...prev]);
            setSelectedTemplateId(newTpl.id);
        } catch (err) {
            console.error('Failed to save template', err);
            alert('Failed to save template.');
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-0 md:p-4 transition-all">
            <div className="bg-card rounded-none md:rounded-2xl shadow-2xl w-full h-full md:h-auto md:max-w-4xl md:max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-4 md:p-6 border-b border-border bg-muted/10 safe-area-top">
                    <div>
                        <h2 className="text-xl md:text-2xl font-bold text-foreground flex items-center gap-2">
                            <Mail className="text-primary" size={24} />
                            Review Invitation Email
                        </h2>
                        <p className="text-xs md:text-sm text-muted-foreground mt-1">
                            This email will be sent to <strong>{employeeData?.email}</strong>
                            {scheduleDate && ` on ${formatDate(scheduleDate)}`}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-muted/50 rounded-lg transition-colors"
                    >
                        <X size={24} className="text-muted-foreground" />
                    </button>
                </div>

                {/* Template Selector */}
                <div className="px-4 md:px-6 py-3 border-b border-border bg-muted/5">
                    <div className="flex items-center gap-3">
                        <FileText size={16} className="text-muted-foreground flex-shrink-0" />
                        <label className="text-sm font-medium text-muted-foreground whitespace-nowrap">Template:</label>
                        <select
                            value={selectedTemplateId || 'default'}
                            onChange={(e) => handleTemplateChange(e.target.value)}
                            className="flex-1 px-3 py-1.5 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 max-w-xs"
                        >
                            <option value="default">Built-in Default</option>
                            {templates.map(t => (
                                <option key={t.id} value={t.id}>
                                    {t.name}{t.is_default ? ' ★' : ''}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-4 md:p-6">
                    {isEditing ? (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-foreground">Edit Email Content</h3>
                                <button
                                    onClick={() => setIsEditing(false)}
                                    className="flex items-center gap-2 px-4 py-2 bg-muted hover:bg-muted/80 rounded-lg text-foreground font-medium transition-colors"
                                >
                                    <Eye size={18} />
                                    Preview
                                </button>
                            </div>

                            {/* Subject Line */}
                            <div>
                                <label className="flex items-center text-sm font-semibold text-foreground mb-2">
                                    Subject Line
                                    <PlaceholderDropdown field="subject" />
                                </label>
                                <input
                                    ref={fieldRefs.subject}
                                    type="text"
                                    value={emailContent.subject}
                                    onChange={(e) => setEmailContent({ ...emailContent, subject: e.target.value })}
                                    onFocus={() => setActiveField('subject')}
                                    className="w-full px-4 py-3 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-background text-foreground"
                                    placeholder="Email subject"
                                />
                            </div>

                            {/* Greeting */}
                            <div>
                                <label className="flex items-center text-sm font-semibold text-foreground mb-2">
                                    Greeting
                                    <PlaceholderDropdown field="greeting" />
                                </label>
                                <input
                                    ref={fieldRefs.greeting}
                                    type="text"
                                    value={emailContent.greeting}
                                    onChange={(e) => setEmailContent({ ...emailContent, greeting: e.target.value })}
                                    onFocus={() => setActiveField('greeting')}
                                    className="w-full px-4 py-3 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-background text-foreground"
                                    placeholder="Dear John,"
                                />
                            </div>

                            {/* Body */}
                            <div>
                                <label className="flex items-center text-sm font-semibold text-foreground mb-2">
                                    Email Body
                                    <PlaceholderDropdown field="body" />
                                </label>
                                <div onFocus={() => setActiveField('body')}>
                                    <RichTextEditor
                                        ref={fieldRefs.body}
                                        value={emailContent.body}
                                        onChange={(value) => setEmailContent({ ...emailContent, body: value })}
                                    />
                                </div>
                            </div>

                            {/* Closing */}
                            <div>
                                <label className="flex items-center text-sm font-semibold text-foreground mb-2">
                                    Closing
                                    <PlaceholderDropdown field="closing" />
                                </label>
                                <div onFocus={() => setActiveField('closing')}>
                                    <RichTextEditor
                                        ref={fieldRefs.closing}
                                        value={emailContent.closing}
                                        onChange={(value) => setEmailContent({ ...emailContent, closing: value })}
                                    />
                                </div>
                            </div>

                            {/* Footer */}
                            <div>
                                <label className="flex items-center text-sm font-semibold text-foreground mb-2">
                                    Footer
                                    <PlaceholderDropdown field="footer" />
                                </label>
                                <div onFocus={() => setActiveField('footer')}>
                                    <RichTextEditor
                                        ref={fieldRefs.footer}
                                        value={emailContent.footer}
                                        onChange={(value) => setEmailContent({ ...emailContent, footer: value })}
                                    />
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="bg-card border border-border rounded-xl shadow-sm">
                            {/* Email Preview */}
                            <div className="p-8 max-w-3xl mx-auto">
                                {/* Subject */}
                                <div className="mb-6 pb-4 border-b border-border">
                                    <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Subject</p>
                                    <h3 className="text-xl font-semibold text-foreground">{substitutePlaceholders(emailContent.subject)}</h3>
                                </div>

                                {/* Email Body */}
                                <div className="space-y-4 text-foreground">
                                    <p className="font-medium">{substitutePlaceholders(emailContent.greeting)}</p>
                                    <div dangerouslySetInnerHTML={{ __html: substitutePlaceholders(emailContent.body) }} />
                                    <div dangerouslySetInnerHTML={{ __html: substitutePlaceholders(emailContent.closing) }} />
                                    <div dangerouslySetInnerHTML={{ __html: substitutePlaceholders(emailContent.footer) }} />
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer Actions */}
                <div className="border-t border-border bg-muted/30 p-4 md:p-6 safe-area-bottom">
                    <div className="flex flex-col xl:flex-row items-center justify-between gap-4">
                        {/* Left Actions */}
                        <div className="flex items-center gap-3 w-full xl:w-auto justify-center xl:justify-start flex-wrap">
                            <button
                                onClick={onClose}
                                className="flex items-center gap-2 px-4 py-2 text-foreground hover:bg-muted rounded-xl font-semibold transition-colors"
                            >
                                <ArrowLeft size={18} />
                                Back
                            </button>

                            {!isEditing && (
                                <button
                                    onClick={() => setIsEditing(true)}
                                    className="flex items-center gap-2 px-4 py-2 bg-background border border-border hover:border-gray-400 rounded-xl font-semibold transition-colors whitespace-nowrap"
                                >
                                    <Edit3 size={18} />
                                    Edit Email
                                </button>
                            )}

                            <button
                                onClick={handleSaveAsTemplate}
                                className="flex items-center gap-2 px-4 py-2 bg-background border border-border hover:border-info/50 rounded-xl font-semibold transition-colors whitespace-nowrap text-blue-600"
                            >
                                <Save size={18} />
                                Save as Template
                            </button>

                            <button
                                onClick={handleResetToDefault}
                                className="flex items-center gap-2 px-4 py-2 text-amber-600 hover:bg-amber-50 rounded-xl font-semibold transition-colors"
                                title="Reset to Default Template"
                            >
                                <RotateCcw size={18} />
                                Reset
                            </button>
                        </div>

                        {/* Right Actions */}
                        <div className="flex items-center gap-3 w-full xl:w-auto justify-center xl:justify-end">
                            {!showScheduleOptions ? (
                                <div className="flex items-center gap-3 flex-wrap justify-center">
                                    <button
                                        onClick={() => setShowScheduleOptions(true)}
                                        className="flex items-center gap-2 px-5 py-3 bg-accent/10 text-accent hover:bg-accent/20 rounded-xl font-semibold transition-colors whitespace-nowrap"
                                    >
                                        <Clock size={20} />
                                        Schedule Send
                                    </button>

                                    <button
                                        onClick={handleSendNow}
                                        disabled={loading}
                                        className="flex items-center gap-2 px-6 py-3 bg-primary hover:bg-primary/90 text-white rounded-xl font-semibold shadow-lg shadow-primary/30 hover:shadow-xl hover:shadow-primary/40 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                                    >
                                        {loading ? (
                                            <>
                                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                                Sending...
                                            </>
                                        ) : (
                                            <>
                                                <Send size={20} />
                                                Send Now
                                            </>
                                        )}
                                    </button>
                                </div>
                            ) : (
                                <div className="flex flex-col sm:flex-row items-center gap-3 bg-card p-2 sm:p-3 rounded-xl border border-border w-full xl:w-auto">
                                    <div className="flex items-center gap-2 w-full sm:w-auto">
                                        <Calendar size={18} className="text-muted-foreground flex-shrink-0" />
                                        <div className="flex gap-2 flex-1 sm:flex-none">
                                            <input
                                                type="date"
                                                value={scheduleDate}
                                                onChange={(e) => setScheduleDate(e.target.value)}
                                                className="w-full sm:w-auto px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none min-w-[130px] bg-background text-foreground"
                                            />
                                            <input
                                                type="time"
                                                value={scheduleTime}
                                                onChange={(e) => setScheduleTime(e.target.value)}
                                                className="w-full sm:w-auto px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-background text-foreground"
                                            />
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
                                        <button
                                            onClick={() => setShowScheduleOptions(false)}
                                            className="px-4 py-2 text-muted-foreground hover:bg-muted rounded-lg font-medium transition-colors"
                                        >
                                            Cancel
                                        </button>

                                        <button
                                            onClick={handleScheduleSend}
                                            disabled={loading}
                                            className="flex items-center gap-2 px-5 py-2 bg-accent hover:bg-accent/90 text-accent-foreground rounded-lg font-semibold shadow-lg shadow-accent/30 hover:shadow-xl hover:shadow-accent/40 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                                        >
                                            {loading ? (
                                                <>
                                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                                    Scheduling...
                                                </>
                                            ) : (
                                                <>
                                                    <Clock size={18} />
                                                    Confirm
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EmailPreviewModal;

